import axios from "axios";
import { PLANNER_BASE_URL } from "../constants";

const api = axios.create({
  baseURL: PLANNER_BASE_URL,
  timeout: 10000, // 10 segundos
});

const normalizeVehicle = (vehicle = {}) => {
  const normalized = {
    ...vehicle,
    vehicleId:
      vehicle?.vehicleId ??
      vehicle?.id ??
      vehicle?.vehicle_id ??
      vehicle?.prefixo ??
      null,
    vehiclePrefix:
      vehicle?.vehiclePrefix ?? vehicle?.prefixo ?? vehicle?.prefix ?? null,
    lat: Number(vehicle?.lat ?? vehicle?.latitude),
    lng: Number(vehicle?.lng ?? vehicle?.lon ?? vehicle?.longitude),
    routeId: vehicle?.routeId ?? vehicle?.route_id ?? null,
    routeShortName:
      vehicle?.routeShortName ??
      vehicle?.route_short_name ??
      vehicle?.line ??
      vehicle?.numeroLinha ??
      null,
    routeLongName:
      vehicle?.routeLongName ?? vehicle?.route_long_name ?? vehicle?.linha ?? null,
    routeColor:
      vehicle?.routeColor ?? vehicle?.route_color ?? vehicle?.linhaCor ?? null,
    directionId:
      vehicle?.directionId ?? vehicle?.direction_id ?? vehicle?.sentido ?? null,
    directionLabel: vehicle?.directionLabel ?? vehicle?.sentidoLabel ?? null,
    directionTerminus:
      vehicle?.directionTerminus ?? vehicle?.sentidoTerminus ?? null,
    tripId: vehicle?.tripId ?? vehicle?.trip_id ?? null,
    tripIdRaw: vehicle?.tripIdRaw ?? vehicle?.trip_id_raw ?? null,
    bearing: Number(vehicle?.bearing),
    operationalStatus:
      vehicle?.operationalStatus ?? vehicle?.status ?? vehicle?.tripStatus ?? null,
    punctuality:
      vehicle?.punctuality ?? vehicle?.pontualidade ?? vehicle?.tripPunctuality ?? null,
    delayMinutes:
      vehicle?.delayMinutes ??
      vehicle?.delay_minutes ??
      vehicle?.atrasoMinutos ??
      null,
  };

  if (!Number.isFinite(normalized.lat) || !Number.isFinite(normalized.lng)) {
    return null;
  }

  if (!normalized.vehicleId) {
    normalized.vehicleId =
      normalized.vehiclePrefix ||
      normalized.tripId ||
      `${normalized.lat.toFixed(6)}:${normalized.lng.toFixed(6)}`;
  }

  if (!Number.isFinite(normalized.bearing)) {
    normalized.bearing = null;
  }

  return normalized;
};

/**
 * Normaliza o payload do GTFS-RT (`/vehicle-positions`).
 * Aceita tanto um array de veículos quanto `{ vehicles, total, filtered }`.
 * Veículos com lat/lng inválidos são descartados (normalizeVehicle → null).
 */
export const normalizeVehiclesPayload = (payload = {}) => {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.vehicles)
    ? payload.vehicles
    : [];
  return raw.map(normalizeVehicle).filter(Boolean);
};

export const busApi = {
  /**
   * Get Routes: lista linhas de ônibus
   */
  getRoutes: (search) =>
    api.get("/api/planner/routes", {
      params: search ? { search } : undefined,
    }),

  /**
   * Get Route Departures: horários de uma linha
   */
  getDeparturesByRoute: (routeId) =>
    api.get(`/api/planner/routes/${routeId}/departures`),

  /**
   * Get Stop Departures: próximos ônibus em uma parada
   */
  getStopDepartures: (stopId) =>
    api.get(`/api/planner/stops/${stopId}/departures`),

  /**
   * Get Nearby Stops: paradas próximas de uma coordenada
   * @param {Object} params
   * @param {number} params.lat
   * @param {number} params.lng
   * @param {number} [params.radius]
   * @param {number} [params.limit]
   */
  getNearbyStops: (params) => api.get("/api/planner/stops/nearby", { params }),

  /**
   * Get Trip Shape: percurso completo + (opcional) paradas por tripId
   * @param {string} tripId
   * @param {Object} [options]
   * @param {boolean} [options.includeStops]
   */
  getTripShape: (tripId, options = {}) =>
    api.get(`/api/planner/trips/${encodeURIComponent(tripId)}/shape`, {
      params: options,
    }),

  /**
   * Get Vehicle Positions: posições em tempo real dos ônibus
   * @param {Object} filters - Filtros opcionais
   * @param {string} filters.routeId - ID de uma linha
   * @param {string} filters.routeIds - IDs de múltiplas linhas (separado por vírgula)
   * @param {string|number} filters.directionId - Sentido ("0"/"1" ou 0/1)
   * @param {string} filters.bounds - Bounding box (minLat,minLng,maxLat,maxLng)
   * @param {number} filters.limit - Limite de veículos
   */
  getVehiclePositions: (filters = {}, config = {}) =>
    api.get("/api/planner/vehicle-positions", { params: filters, ...config }),

  // ─────────────────────────────────────────────────────────────────────────
  // Camada operacional Cittati — ESTACIONADA (ver docs/analise-modulo-mapa.md §6).
  // O backend deste cliente NÃO expõe /api/planner/operational/*. Estes stubs
  // resolvem vazio SEM rede (evitam 404 a cada chamada) até a integração ser
  // retomada com credenciais reais. Remover de vez em T20 junto aos call sites.
  // O `getOperationalMapSnapshot` foi removido: o mapa usa GTFS-RT direto.
  // ─────────────────────────────────────────────────────────────────────────
  getOperationalTrips: () => Promise.resolve({ data: { operationalTrips: [] } }),
  getOperationalTripsByDate: () =>
    Promise.resolve({ data: { operationalTrips: [] } }),
  getOperationalTripsByLine: () =>
    Promise.resolve({ data: { operationalTrips: [] } }),
  getOperationalTripsByVehicle: () =>
    Promise.resolve({ data: { operationalTrips: [] } }),
};

export default busApi;
